import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Adresse } from '@core/models/adresse.model';
import { AdresseRetour } from '@core/models/adresseRetour.model';
import { CampagneExt } from '@core/models/campagne-ext.model';
import { Campagne } from '@core/models/campagne.model';
import { ChampsFusion } from '@core/models/champsFusion.model';
import { Commande } from '@core/models/commande.model';
import { FichiersCreation } from '@core/models/FichiersCreation.model';
import { Support } from '@core/models/support.model';
import { HelpComponent } from 'app/campagne/components';
import { catchError, defaultIfEmpty, forkJoin, from, of } from 'rxjs';
import { AdresseService } from './adresse.service';
import { CampagneAdressesService } from './campagne-adresses.service';
import { CampagneIdentificationService } from './campagne-identification.service';
import { CampagnePlanificationService } from './campagne-planification.service';
import { CampagneTypologieService } from './campagne-typologie.service';
import { CampagneService } from './campagne.service';
import { ChampsFusionService } from './champs-fusion.service';
import { CommandeService } from './commande.service';
import { FichierCreaService } from './fichier-crea.service';
import { GlobalDataService } from './global-data.service';
import { PersonalisationSfService } from './personalisation-sf.service';
import { PersonalisationService } from './personalisation.service';
import { ProduitService } from './produit.service';
import { SharedListSalesforceService } from './shared-list-salesforce.service';
import {
  CommandeStatusPayload,
  normalizeStatutCommandeList,
  StatutCommandeService,
} from './statut-commande.service';
import { AdressesDestinatairesService } from './adresses-destinataires.service';
import {
  mapPersistedChampsFusion,
  mergePersistedChampsFusion,
  normalizePaveAdresseFlag,
  resolvePersistedChampsFusion,
} from '@core/utils/champs-fusion.utils';

export {
  mapPersistedChampsFusion,
  mergePersistedChampsFusion,
  normalizePaveAdresseFlag,
  resolvePersistedChampsFusion,
} from '@core/utils/champs-fusion.utils';

export function mapContactFieldsToChampsFusion(contactFields: any = []): ChampsFusion[] {
  const normalizedFields = Array.isArray(contactFields)
    ? contactFields
    : (contactFields?.ContactFields || contactFields?.contactFields || []);

  return (normalizedFields || []).map((field: any) => ({
    Id: field?.Id,
    ChampFusion: field?.FieldName || field?.fieldName,
    internalName: field?.internalName,
  } as ChampsFusion));
}

export function isCampaignDetailsReady(
  isLoaded: boolean,
  loadedCampaignId: string | undefined,
  routeCampaignId: string | null
): boolean {
  return isLoaded && !!routeCampaignId && loadedCampaignId === routeCampaignId;
}

export type CampaignOrderStatus = CommandeStatusPayload;

export type CampaignPercentageMapping = Record<
  string,
  string | number | null | undefined
>;

/** Normalize the two historical payload shapes returned by the status API. */
export function normalizeCampaignOrderStatuses(response: unknown): CampaignOrderStatus[] {
  const rawStatuses = (response as { StatutCommande?: unknown } | null)?.StatutCommande;
  return normalizeStatutCommandeList(rawStatuses);
}

/** Resolve the latest business percentage, including the first status. */
export function resolveCampaignPercentage(
  statuses: CampaignOrderStatus[],
  mapping: CampaignPercentageMapping
): string {
  for (let index = statuses.length - 1; index >= 0; index--) {
    const codeStatut = statuses[index]?.codeStatut;
    if (!codeStatut) {
      continue;
    }

    const rawPercentage = mapping[codeStatut];
    if (rawPercentage === null || rawPercentage === undefined || rawPercentage === '') {
      continue;
    }

    const percentage = Number(rawPercentage);
    if (Number.isFinite(percentage) && percentage > 0) {
      return String(percentage);
    }
  }

  return '0';
}

export function normalizeCampaignFiles(files: FichiersCreation[] = []): FichiersCreation[] {
  const seen = new Set<string>();
  return (files || []).reduce((normalized: FichiersCreation[], file: FichiersCreation) => {
    const key = file?.IdfichierCreation
      ? `id:${file.IdfichierCreation}`
      : `file:${file?.NomFichier || ''}:${file?.FichierCreation || ''}`;
    if (seen.has(key)) {
      return normalized;
    }
    seen.add(key);
    normalized.push({ ...file });
    return normalized;
  }, []);
}

export type CampaignDetailsView =
  | 'campagne-details'
  | 'suivi-campagne-recurrente'
  | 'suivi-campagne-une-seule-fois';

export interface CampaignNavigationTarget {
  view: CampaignDetailsView;
  campaignId: string;
}

const campaignDetailsViews = new Set<CampaignDetailsView>([
  'campagne-details',
  'suivi-campagne-recurrente',
  'suivi-campagne-une-seule-fois',
]);

export function resolveCampaignNavigationTarget(commands: any[] | null): CampaignNavigationTarget | null {
  const view = typeof commands?.[0] === 'string' ? commands[0] as CampaignDetailsView : null;
  const campaignId = commands?.[1] == null ? '' : String(commands[1]).trim();
  if (
    !view ||
    !campaignDetailsViews.has(view) ||
    !campaignId ||
    campaignId === 'undefined' ||
    campaignId === 'null'
  ) {
    return null;
  }
  return { view, campaignId };
}

export function resolveCampaignDetailsView(campagne: Campagne): CampaignDetailsView {
  const status = String(campagne?.CodeStatut || '').trim();
  if (!status || status === 'EN_CREATION' || status === 'A_CORRIGER') {
    return 'campagne-details';
  }
  const frequency = String(campagne?.FrequenceEnvoi || '').toLowerCase();
  return frequency.includes('currente')
    ? 'suivi-campagne-recurrente'
    : 'suivi-campagne-une-seule-fois';
}

@Injectable({
  providedIn: 'root'
})
export class CampagneDetailsService {

  constructor(
    public router: Router,
    private domSanitizer: DomSanitizer,
    public _GlobalDataService: GlobalDataService,
    private _CampagneService: CampagneService,
    private _AdresseService: AdresseService,
    public _HelpComponent: HelpComponent,
    private _FichierCreaService: FichierCreaService,
    private _ProduitService: ProduitService,
    private _ChampsFusionService: ChampsFusionService,
    private _SharedListSalesforceService: SharedListSalesforceService,
    private _StatutCommandeService: StatutCommandeService,
    private _CommandeService: CommandeService,
    private _CampagnePlanificationService: CampagnePlanificationService,
    public personalisationSfService: PersonalisationSfService,
    private personalisationService: PersonalisationService,
    private adressesDestinatairesService: AdressesDestinatairesService,
  ) { }

  navigate(view: string, IdCampagne?: string) {
    let path = "/campagne/home";
    let context = 'CAMPAGNE';

    if (this._GlobalDataService.isStandaloneContext()) {
      path = "/standalone/home"
      context = 'STANDALONE';
    }
        this._GlobalDataService.saveNavViewState([view, IdCampagne], context);
    // avoid navigating if we're already on the same view for the same campaign
    const currentUrl = this.router.url || '';
    if (IdCampagne && currentUrl.includes(view) && currentUrl.includes(IdCampagne)) {
      return;
    }
    this.router.navigate([path, { outlets: { 'navview': [view, IdCampagne] } }], {
      queryParams: this._GlobalDataService.objectParams, queryParamsHandling: "merge",
      skipLocationChange: false
    }).then((e) => {
    });
  }

  public getCampaignDetailsView(campagne: Campagne): string {
    return resolveCampaignDetailsView(campagne);
  }

  public openCampaign(campagne: Campagne, preferredView?: CampaignDetailsView): void {
    if (!campagne?.IdCampagne) {
      return;
    }

    const campaignId = campagne.IdCampagne;
    const campaignFromList = { ...campagne };
    this._GlobalDataService.Campagne = campaignFromList;

    if (this._GlobalDataService.CONTEXT === 'ADMIN') {
      this.router.navigate(['/administration/suivi-detail-campagne', campagne.IdCampagne], {
        queryParams: this._GlobalDataService.objectParams,
        queryParamsHandling: 'merge',
      });
      return;
    }

    // The tracking components must never be instantiated with the previous
    // campaign's extended state. Load the complete campaign first; the details
    // loader will navigate only after CampagneExt has been hydrated.
    this._GlobalDataService.CampagneExt = new CampagneExt();
    this._GlobalDataService.isLoaded = false;

    this._CampagneService.get(campaignId).subscribe({
      next: (response: any) => {
        // Ignore a late response if another campaign was selected meanwhile.
        if (this._GlobalDataService.Campagne?.IdCampagne !== campaignId) {
          return;
        }

        this._GlobalDataService.Campagne = {
          ...campaignFromList,
          ...(response?.campagne || {}),
        };
        this.gotoDetailCampagne();
      },
      error: (error) => {
        console.error(`Unable to load campaign ${campaignId} before navigation`, error);
        this._GlobalDataService.isLoaded = true;
        this._GlobalDataService.openSnackBarInfo(
          "Impossible de charger le détail de la campagne. Merci de réessayer."
        );
      },
    });
  }

  downloadFile(fileName: string) {
    return this._FichierCreaService.download(fileName, this._GlobalDataService.Campagne.IdCampagne)
      .subscribe(response => {
        var blob = new Blob([response], { type: response.type });
        //Create a link element, hide it, direct 
        //it towards the blob, and then 'click' it programatically

        //Create a DOMString representing the blob 
        //and point the link element towards it


        if (blob.type.startsWith('image')) {
          let objectURL = URL.createObjectURL(blob);
          this._GlobalDataService.CampagneExt.FichiersCreationsURL[fileName] = this.domSanitizer.bypassSecurityTrustUrl(objectURL);
          this._GlobalDataService.CampagneExt.FichiersCreationsType[fileName] = 'image';

        }
        else {
          const fileReader = new FileReader();
          fileReader.onload = () => {
            this._GlobalDataService.CampagneExt.FichiersCreationsURL[fileName] = new Uint8Array(fileReader.result as ArrayBuffer);
            this._GlobalDataService.CampagneExt.FichiersCreationsType[fileName] = blob.type;
          };
          fileReader.readAsArrayBuffer(blob);
        }
      });
  }


  initAvailableCamapgnes() {
    this._GlobalDataService.availableCamapgnes.forEach((campagne: Campagne) => {
      campagne.Adresse = new AdresseRetour(this._GlobalDataService.Annonceur);
      let arr = this._GlobalDataService.availableAdresses.filter(adresse => adresse.IdAdresse == campagne.IdAdresse);
      if (arr.length > 0) campagne.Adresse = arr[0];
    })
  }

  async gotoDetailCampagne() {
    const campaignId = this._GlobalDataService.Campagne.IdCampagne;

    this._GlobalDataService.Campagne.isDirty = false;

    // init data
    this._GlobalDataService.isLoaded = false;
    // A restored draft is authoritative for local, not-yet-saved wizard state.
    const hasRestoredCampagneExtDraft = this._GlobalDataService.applyPendingCampaignExtDraft();
    
    const restoredChampsFusion = hasRestoredCampagneExtDraft
      ? [...(this._GlobalDataService.CampagneExt?.ChampsFusion || [])]
      : [];

    // Ensure CampagneExt exists and has the expected arrays
    if (!this._GlobalDataService.CampagneExt) {
      this._GlobalDataService.CampagneExt = new CampagneExt();
    }
    this._GlobalDataService.CampagneExt.currentStatutCommandeArray = this._GlobalDataService.CampagneExt.currentStatutCommandeArray || [];

    // Options de retour
    this._GlobalDataService.setOptionRetour();


    // Statut Mapping
    await this._GlobalDataService.getStatutMapping();

    // A user can select another row while the first campaign is still loading.
    // In that case this obsolete load must not mutate the new campaign state.
    if (this._GlobalDataService.Campagne.IdCampagne !== campaignId) {
      return;
    }

    // EmailNotification
    if (typeof this._GlobalDataService.Campagne.EmailNotification === "undefined") this._GlobalDataService.Campagne.EmailNotification = "";

    // this.showSpinner = true;

    this._GlobalDataService.produits = new Array<any>();
    this._GlobalDataService.availableAdresses = new Array<Adresse>();
    if (!Array.isArray(this._GlobalDataService.availableCamapgnes)) {
      this._GlobalDataService.availableCamapgnes = [];
    } else {
      this._GlobalDataService.availableCamapgnes.length = 0;
    }

    if (this._GlobalDataService.isEloquaContext()) {
      this._GlobalDataService.availableEloquaChampsFusion = new Array<ChampsFusion>();
    }

    if (!hasRestoredCampagneExtDraft) {
      this._GlobalDataService.CampagneExt.Produit = {};
      this._GlobalDataService.CampagneExt.Support = new Support();
      this._GlobalDataService.CampagneExt.Enveloppe = new Support();
      this._GlobalDataService.CampagneExt.Adresse = new AdresseRetour(this._GlobalDataService.Annonceur);
      this._GlobalDataService.CampagneExt.FichiersCreation = [];
      this._GlobalDataService.CampagneExt.ChampsFusion = [];
      this._GlobalDataService.CampagneExt.FichiersCreationsURL = {};
      this._GlobalDataService.CampagneExt.FichiersCreationsType = {};
      this._GlobalDataService.CampagneExt.NbFichiersCreation = 0;
      this._GlobalDataService.CampagneExt.NbFichiersPersonnalisation = 0;
      this._GlobalDataService.CampagneExt.NbFichiersLogo = 0;
      this._GlobalDataService.CampagneExt.Commandes = [];
      this._GlobalDataService.CampagneExt.CurrentCommande = new Commande();
      this._GlobalDataService.CampagneExt.VitessesProduit = ['Classique', 'Rapide'];
    } else {
      this._GlobalDataService.CampagneExt.Commandes = this._GlobalDataService.CampagneExt.Commandes || [];
      this._GlobalDataService.CampagneExt.CurrentCommande = this._GlobalDataService.CampagneExt.CurrentCommande || new Commande();
      this._GlobalDataService.CampagneExt.VitessesProduit =
        this._GlobalDataService.CampagneExt.VitessesProduit?.length > 0
          ? this._GlobalDataService.CampagneExt.VitessesProduit
          : ['Classique', 'Rapide'];
    }

    if (!this._GlobalDataService.Campagne.Percentage) {
      this._GlobalDataService.Campagne.Percentage = "0";
    }

    // init des dates
    if (!this._GlobalDataService.Campagne.DateRemiseImprimeur) this._GlobalDataService.Campagne.DateRemiseImprimeur = "null";
    if (!this._GlobalDataService.Campagne.DateDeDebut) this._GlobalDataService.Campagne.DateDeDebut = "null";
    if (!this._GlobalDataService.Campagne.DateDeFin) this._GlobalDataService.Campagne.DateDeFin = "null";
    if (!this._GlobalDataService.Campagne.DateTheoriqueDepotLaPoste) this._GlobalDataService.Campagne.DateTheoriqueDepotLaPoste = "null";


    const hasCampaignId = !!campaignId && campaignId !== '0';
    const hasProductId = !!this._GlobalDataService.Campagne.IdProduit && this._GlobalDataService.Campagne.IdProduit !== '0';
    const isEditableCampaign = !this._GlobalDataService.Campagne.CodeStatut ||
      this._GlobalDataService.Campagne.CodeStatut === 'EN_CREATION' ||
      this._GlobalDataService.Campagne.CodeStatut === 'A_CORRIGER';
    const hasStandaloneContactFile =
      this._GlobalDataService.isStandaloneContext() &&
      this._GlobalDataService.Campagne.importOption !== 'sftp' &&
      !!this._GlobalDataService.Campagne.importFile;

    const emptyFilesResponse = of({ fichierCreation: [] });
    const emptyFusionResponse = of({ ChampsFusion: [] });
    const emptyCommandsResponse = of({ commande: [] });
    const emptyContactFieldsResponse = of({ ContactFields: [] });

    let produits$ = from(this._ProduitService.getList()).pipe(
      catchError(() => of({ produit: [] }))
    );
    let availableEloquaChampsFusion$ = from(this._ChampsFusionService.listChampsDisponible()).pipe(
      catchError(() => emptyContactFieldsResponse)
    );
    let availableStandaloneChampsFusion$ = hasCampaignId && hasStandaloneContactFile
      ? from(this._ChampsFusionService.listChampsDisponibleStandalone()).pipe(
        catchError(() => emptyContactFieldsResponse)
      )
      : emptyContactFieldsResponse;
    const annonceurId = this._GlobalDataService.getResolvedAnnonceurId();
    let availableAdresses$ = from(this._AdresseService.list(annonceurId)).pipe(
      catchError(() => of({ adresse: [] }))
    );
    let availableCampaignsRequestFailed = false;
    let availableCamapgnes$ = from(this._CampagneService.getListAvailable(annonceurId)).pipe(
      catchError(() => {
        availableCampaignsRequestFailed = true;
        return of({ campagne: [] });
      })
    );


    let produit$ = this._GlobalDataService.Campagne.IdProduit && this._GlobalDataService.Campagne.IdProduit !== '0'
      ? from(this._ProduitService.getProduitById(this._GlobalDataService.Campagne.IdProduit)).pipe(
        catchError(() => of({ produit: {} }))
      )
      : of({ produit: {} });

    let fichiersCreation$ = hasCampaignId
      ? from(this._FichierCreaService.list(campaignId)).pipe(
        catchError(() => emptyFilesResponse)
      )
      : emptyFilesResponse;
    let sharedLists$ = from(this._SharedListSalesforceService.getSharedLists()).pipe(
      catchError(() => of({ entry: [] }))
    );
    let champsFusion$ = hasCampaignId
      ? from(this._ChampsFusionService.list(campaignId)).pipe(
        catchError(error => {
          console.error(`Unable to load fusion fields for campaign ${campaignId}`, error);
          this._GlobalDataService.openSnackBarInfo(
            "Impossible de charger les champs de personnalisation et du pavé adresse. Merci de réessayer."
          );
          return emptyFusionResponse;
        })
      )
      : emptyFusionResponse;
    let commandes$$ = hasCampaignId
      ? from(this._CommandeService.getListByIdCampagne(campaignId)).pipe(
        catchError(() => emptyCommandsResponse)
      )
      : emptyCommandsResponse;

    this._GlobalDataService.availableCampagnesLoading = true;
    this._GlobalDataService.availableCampagnesError = false;

    let joindMap = {};
    let joined$: any;
    if (this._GlobalDataService.isSalesforceContext()) {
      if (isEditableCampaign) {
        joindMap = { fichiersCreation: fichiersCreation$, champsFusion: champsFusion$, sharedLists: sharedLists$, availableCamapgnes: availableCamapgnes$, produits: produits$, availableAdresses: availableAdresses$ };
      }
      else {
        joindMap = { fichiersCreation: fichiersCreation$, champsFusion: champsFusion$, produits: produits$, availableCamapgnes: availableCamapgnes$, availableAdresses: availableAdresses$ };
      }
    }
    else if (this._GlobalDataService.isEloquaContext()) {
      if (isEditableCampaign) {
        joindMap = { fichiersCreation: fichiersCreation$, champsFusion: champsFusion$, availableEloquaChampsFusion: availableEloquaChampsFusion$, availableAdresses: availableAdresses$, availableCamapgnes: availableCamapgnes$, produits: produits$ };
      }
      else {
        joindMap = { fichiersCreation: fichiersCreation$, champsFusion: champsFusion$, produits: produits$, availableCamapgnes: availableCamapgnes$, availableAdresses: availableAdresses$ };
      }
    }
    else if (this._GlobalDataService.isStandaloneContext()) {
      joindMap = {
        fichiersCreation: fichiersCreation$,
        produits: produits$,
        availableCamapgnes: availableCamapgnes$,
        availableAdresses: availableAdresses$,
        availableEloquaChampsFusion: availableStandaloneChampsFusion$,
        champsFusion: champsFusion$
      };
    }

    if (hasProductId) {
      joindMap['produit'] = produit$;
    }

    if (hasCampaignId && !isEditableCampaign) {
      joindMap['commandes'] = commandes$$;
    }

    joined$ = forkJoin(joindMap).pipe(defaultIfEmpty({}));


    joined$.subscribe(
      (result: any) => {
        if (this._GlobalDataService.Campagne.IdCampagne !== campaignId) {
          return;
        }

        // availableEloquaChampsFusion
        if (
          (this._GlobalDataService.isEloquaContext() || this._GlobalDataService.isStandaloneContext()) &&
          result.availableEloquaChampsFusion
        ) {
          this._GlobalDataService.availableEloquaChampsFusion = mapContactFieldsToChampsFusion(
            result.availableEloquaChampsFusion
          );
        }

        // availableAdresses
        this._GlobalDataService.availableAdresses = result.availableAdresses?.adresse || [];

        // availableCamapgnes
        this._GlobalDataService.availableCampagnesLoading = false;
        this._GlobalDataService.availableCampagnesError = availableCampaignsRequestFailed;
      if (isEditableCampaign) {
          const availableCampaigns = Array.isArray(result.availableCamapgnes?.campagne)
            ? result.availableCamapgnes.campagne
            : [];
          const filteredAvailableCampaigns = availableCampaigns.filter(
            (campagne: Campagne) => !!campagne && campagne.NomCampagne !== ''
          );
          this._GlobalDataService.availableCamapgnes.splice(
            0,
            this._GlobalDataService.availableCamapgnes.length,
            ...filteredAvailableCampaigns,
          );
          this.initAvailableCamapgnes();
        }

        // produits
        this._GlobalDataService.produits = result.produits?.produit || [];

        // Produit
        if (hasProductId) {
          this._GlobalDataService.CampagneExt.Produit = result.produit?.produit || {};
          this.initProduit();
        }

        // Adress Retour
        if (this._GlobalDataService.Campagne.IdAdresse != '0') {
          const filteredAdresses = this._GlobalDataService.availableAdresses.filter((a: AdresseRetour) => a.IdAdresse === this._GlobalDataService.Campagne.IdAdresse)
          if (filteredAdresses.length > 0) this._GlobalDataService.CampagneExt.Adresse = filteredAdresses[0];
        }

        // Campagne parente Duplication
        if (this._GlobalDataService.Campagne.IdCampagneCopie != '0') {
          const CampagneParente = this._GlobalDataService.availableCamapgnes.filter((c: Campagne) => c.IdCampagne == this._GlobalDataService.Campagne.IdCampagneCopie);
          if (CampagneParente.length > 0) this._GlobalDataService.Campagne.NomCampagneParente = CampagneParente[0].NomCampagne;
        }

        // fichierCreation
        const backendFiles = Array.isArray(result.fichiersCreation?.fichierCreation)
          ? result.fichiersCreation.fichierCreation
          : result.fichiersCreation?.fichierCreation
            ? [result.fichiersCreation.fichierCreation]
            : [];
        if (backendFiles.length > 0) {
          this._GlobalDataService.CampagneExt.FichiersCreation = normalizeCampaignFiles(
            backendFiles
          );
          this._GlobalDataService.CampagneExt.NbFichiersCreation = this._GlobalDataService.getNbFichiersCreation();
          this._GlobalDataService.CampagneExt.NbFichiersPersonnalisation = this._GlobalDataService.getNbFichiersPersonnalisation();
          this._GlobalDataService.CampagneExt.NbFichiersLogo = this._GlobalDataService.getNbFichiersLogo();

          // desactivation du preview @a activer si besoin  
          // if (this._GlobalDataService.Campagne.CodeStatut === "EN_CREATION" ||
          //   this._GlobalDataService.Campagne.CodeStatut === "A_CORRIGER") {
          //   this._GlobalDataService.CampagneExt.FichiersCreation.forEach(f => {
          //     this.downloadFile(f.NomFichier);
          //   })
          // }
        }

        // sharedLists
        if (this._GlobalDataService.isSalesforceContext()) {
        if (isEditableCampaign) {
            this._GlobalDataService.sharedLists = result.sharedLists.entry;
          }
        }

        // ChampsFusion  

        
const backendChampsFusion = mapPersistedChampsFusion(result.champsFusion);

/**
 * Si un draft restauré existe,
 * on le conserve.
 */
this._GlobalDataService.CampagneExt.ChampsFusion = resolvePersistedChampsFusion(
  hasRestoredCampagneExtDraft,
  restoredChampsFusion,
  backendChampsFusion
);
        this._GlobalDataService.CampagneExt.IdCampagne = campaignId;

if (
  this._GlobalDataService.isSalesforceContext() &&
  (
    this._GlobalDataService.Campagne.CodeStatut === "EN_CREATION" ||
    this._GlobalDataService.Campagne.CodeStatut === "A_CORRIGER"
  )
) {
  this.personalisationSfService.initialise();
}

          
      

        // Rehydrate the dual-list services from the saved campaign draft and the freshly loaded available options.
         this.adressesDestinatairesService.initialise();

this.personalisationService.initialise();


        // Fil d'Ariane
        this._GlobalDataService.checkStepCompletion();
        if (!hasCampaignId) {
          this._CampagnePlanificationService.reset();
          this._GlobalDataService.isLoaded = true;
          return;
        }

        // Campagne Récurrente
        if (
          this._GlobalDataService.Campagne.CodeStatut != "EN_CREATION" &&
          this._GlobalDataService.Campagne.CodeStatut != "A_CORRIGER" && 
          this._GlobalDataService.Campagne.FrequenceEnvoi === 'Récurrente'
        ) {
          if (result.commandes.commande.length > 0) {
            this._GlobalDataService.CampagneExt.Commandes = result.commandes.commande;
            this._GlobalDataService.CampagneExt.CurrentCommande = result.commandes.commande[0];

            this._StatutCommandeService.getStatutsByIdCommandeAdmin(this._GlobalDataService.CampagneExt.CurrentCommande.IdCommande)
              .subscribe(
                response => {
                  if (this._GlobalDataService.Campagne.IdCampagne !== campaignId) {
                    return;
                  }

                  const listeStatutCommande = normalizeCampaignOrderStatuses(response);
                  if (listeStatutCommande.length > 0) {

                    let currentStatutCommandeArray = listeStatutCommande.filter((s: any) => ['CONFORMITEOK', 'BAT_AVAILABLEOK', 'BAT_ACCEPTEDOK', 'BAT_REJECTEDOK'].indexOf(s.codeStatut) > -1)
                    let currentStatutCommandeBATOKArray = currentStatutCommandeArray.filter((s: any) => ['BAT_ACCEPTEDOK'].indexOf(s.codeStatut) > -1);

                    if (currentStatutCommandeBATOKArray.length >= 1)
                      this._GlobalDataService.Campagne.DateValidationBAT = currentStatutCommandeBATOKArray[0]['dateModification'] as string;

                    if (currentStatutCommandeArray.length >= 1) {
                      this._GlobalDataService.CampagneExt.CurrentStatutCommande = currentStatutCommandeArray[currentStatutCommandeArray.length - 1] as any;

                      this._GlobalDataService.CampagneExt.CurrentStatutCommande.StatutLibelle = this._GlobalDataService.mappingStatut[this._GlobalDataService.CampagneExt.CurrentStatutCommande.codeStatut];

                    }
                  }

                  this._GlobalDataService.isLoaded = true;
                  this.navigate('suivi-campagne-recurrente', campaignId);
                },
                () => {
                  if (this._GlobalDataService.Campagne.IdCampagne !== campaignId) {
                    return;
                  }
                  this._GlobalDataService.isLoaded = true;
                  this.navigate('suivi-campagne-recurrente', campaignId);
                }
              );
          } else {
            this._GlobalDataService.isLoaded = true;
            this.navigate('suivi-campagne-recurrente', campaignId);
          }
        }
        // Campagne Une seule fois
        else if (
          this._GlobalDataService.Campagne.CodeStatut != "EN_CREATION" &&
          this._GlobalDataService.Campagne.CodeStatut != "A_CORRIGER" &&
          this._GlobalDataService.Campagne.FrequenceEnvoi === 'Une seule fois'
        ) {
          if (result.commandes.commande.length > 0) {
            this._GlobalDataService.CampagneExt.CurrentCommande = result.commandes.commande[0];

            this._StatutCommandeService
              .getStatutsByIdCommandeAdmin(this._GlobalDataService.CampagneExt.CurrentCommande.IdCommande)
              .subscribe(
                response => {
                  if (this._GlobalDataService.Campagne.IdCampagne !== campaignId) {
                    return;
                  }

                  const listeStatutCommande = normalizeCampaignOrderStatuses(response);
                  this._GlobalDataService.CampagneExt.currentStatutCommandeArray = listeStatutCommande as any;
                  this._GlobalDataService.Campagne.Percentage = resolveCampaignPercentage(
                    listeStatutCommande,
                    this._GlobalDataService.mappingPercentage
                  );
                  this._GlobalDataService.applyPendingCampaignExtDraft();
                  this._GlobalDataService.checkStepCompletion();
                  this._GlobalDataService.isLoaded = true;
                  this.navigate('suivi-campagne-une-seule-fois', campaignId);
                },
                error => {
                  if (this._GlobalDataService.Campagne.IdCampagne !== campaignId) {
                    return;
                  }
                  this._GlobalDataService.CampagneExt.currentStatutCommandeArray = [];
                  this._GlobalDataService.Campagne.Percentage = '0';
                  this._GlobalDataService.isLoaded = true;
                  this.navigate('suivi-campagne-une-seule-fois', campaignId);
                }
              );
          } else {
            this._GlobalDataService.CampagneExt.currentStatutCommandeArray = [];
            this._GlobalDataService.Campagne.Percentage = '0';
            this._GlobalDataService.isLoaded = true;
            this.navigate('suivi-campagne-une-seule-fois', campaignId);
          }
        }
        else {
          this._GlobalDataService.isLoaded = true;
          this._CampagnePlanificationService.reset();
          if (!this._GlobalDataService.isNewCampaignWizardRoute()) {
            this.navigate('campagne-details', this._GlobalDataService.Campagne.IdCampagne);
          }
        }
      },
      error => {
        if (this._GlobalDataService.Campagne.IdCampagne !== campaignId) {
          return;
        }
        console.error('Campagne details loading failed:', error);
        this._GlobalDataService.availableCampagnesLoading = false;
        this._GlobalDataService.availableCampagnesError = true;
        this._GlobalDataService.applyPendingCampaignExtDraft();
        this._GlobalDataService.checkStepCompletion();
        this._GlobalDataService.isLoaded = true;
      }
    );
  }

  initProduit() {
    this._GlobalDataService.CampagneExt.VitessesProduit = this._GlobalDataService.getVitessesProduit();

    // const filteredProduits = this._GlobalDataService.produits.filter((p: any) => p.IdProduit === this._GlobalDataService.Campagne.IdProduit)
    // if (filteredProduits.length > 0) this._GlobalDataService.CampagneExt.Produit = filteredProduits[0];

    const LibelleGrammageSupport = this._GlobalDataService.getLibelleProduit('CodeGrammageSupport', this._GlobalDataService.Campagne.CodeGrammageSupport, 'GrammageSupport');
    const LibelleFinitionSupport = this._GlobalDataService.getLibelleProduit('CodeFinitionSupport', this._GlobalDataService.Campagne.CodeFinitionSupport, 'FinitionSupport');
    const LibelleSupport = this._GlobalDataService.getLibelleProduit('CodeSupport', this._GlobalDataService.Campagne.CodeSupport, 'support');
    const LibelleDimensionsSupport = this._GlobalDataService.getLibelleProduit('CodeDimensionsSupport', this._GlobalDataService.Campagne.CodeDimensionsSupport, 'DimensionsSupport');

    const LibelleGrammageEnveloppe = this._GlobalDataService.getLibelleProduit('CodeGrammageEnveloppe', this._GlobalDataService.Campagne.CodeGrammageEnveloppe, 'GrammageEnveloppe');
    const LibelleFinitionEnveloppe = this._GlobalDataService.getLibelleProduit('CodeFinitionEnveloppe', this._GlobalDataService.Campagne.CodeFinitionEnveloppe, 'FinitionEnveloppe');
    const LibelleEnveloppe = this._GlobalDataService.getLibelleProduit('CodeEnveloppe', this._GlobalDataService.Campagne.CodeEnveloppe, 'enveloppe');
    const LibelleDimensionsEnveloppe = this._GlobalDataService.getLibelleProduit('CodeDimensionsEnveloppe', this._GlobalDataService.Campagne.CodeDimensionsEnveloppe, 'DimensionsEnveloppe');

    this._GlobalDataService.CampagneExt.Support = {
      support: LibelleSupport,
      dimension: LibelleDimensionsSupport,
      grammage: LibelleGrammageSupport,
      finition: LibelleFinitionSupport,
      typologie: "",
      Encre: "",
    } as Support

    this._GlobalDataService.CampagneExt.Enveloppe = {
      support: LibelleEnveloppe,
      dimension: LibelleDimensionsEnveloppe,
      grammage: LibelleGrammageEnveloppe,
      finition: LibelleFinitionEnveloppe,
      typologie: "",
      Encre: "",
    } as Support
  }

}
